<script setup lang="ts">
import { usePlayerStore } from '~/stores/usePlayerStore'
import type { Podcast } from '~/types/podcasts'
import type { Episode, EpisodeListItem } from '~/types/episodes'
import { fetchAudioAd } from '~/api/audioAd.api'
import { toast } from '~/components/ui/toast'

const {
  podcast,
  episode,
  isBase = false,
} = defineProps<{
  podcast: Podcast
  episode: Episode | EpisodeListItem
  isBase?: boolean
}>()

const playbackTrackingParams = {
  content_type: 'episode',
  content_group: podcast.external ? 'Third Party Podcasts' : 'Zeno Podcasts',
  content_id: episode.id,
  content_path: `/podcast/${podcast.identifier}/episodes/${episode.identifier}/`,
}

const playerStore = usePlayerStore()

const loading = ref(false)
const isCurrent = computed(() => playerStore.media?.id === episode.id)

const fetchPreRoll = async () => {
  const params = {
    contentId: podcast.id,
    contentName: podcast.title,
    contentLanguage: podcast.language,
    contentEpisode: episode.title,
  }

  return await fetchAudioAd(params)
}

const handlePlay = async () => {
  window.gtag('event', isCurrent.value ? 'content_resume' : 'content_play', playbackTrackingParams)

  if (isCurrent.value) {
    playerStore.play()
    return
  }

  playerStore.isLoading = true
  loading.value = true

  try {
    const preRoll = await fetchPreRoll()

    if (preRoll) {
      playerStore.preRoll = preRoll
    }

    playerStore.media = {
      id: episode.id,
      podcastId: podcast.id,
      title: episode.title,
      subTitle: podcast.title,
      logo: episode.logo,
      route: podcast.route,
      source: episode.mediaUrl,
      sourceType: 'file',
      mediaType: 'episode',
    }

    playerStore.play()
  } catch (error) {
    console.log(error)
    toast({
      title: 'Error',
      description: 'An error occurred while trying to play the episode.',
      variant: 'destructive',
    })
  } finally {
    playerStore.isLoading = false
    loading.value = false
  }
}

const handlePause = () => {
  window.gtag('event', 'content_pause', playbackTrackingParams)

  playerStore.pause()
}
</script>

<template>
  <Button
    v-if="(isCurrent && playerStore.isLoading) || loading"
    class="btn-playback"
    :class="[isBase ? 'btn-playback-main' : 'btn-playback-list']"
  >
    <Icon size="30" name="mingcute:loading-3-fill" class="animate-spin" />
    <span v-if="episode.duration.length">{{ episode.duration }}</span>
  </Button>

  <Button
    v-else-if="!playerStore.isPlaying || !isCurrent"
    :variant="isBase ? 'default' : 'secondary'"
    class="btn-playback"
    :class="[isBase ? 'btn-playback-main' : 'btn-playback-list']"
    @click="handlePlay"
  >
    <Icon size="30" name="ic:round-play-arrow" />
    <span v-if="episode.duration.length">{{ episode.duration }}</span>
  </Button>

  <Button
    v-else-if="playerStore.isPlaying && isCurrent"
    class="btn-playback"
    :class="[isBase ? 'btn-playback-main' : 'btn-playback-list']"
    @click="handlePause"
  >
    <Icon size="30" name="ic:round-pause" />
    <span v-if="episode.duration.length">{{ episode.duration }}</span>
  </Button>
</template>

<style lang="postcss" scoped>
.btn-playback {
  @apply flex flex-row items-center rounded-full text-base font-semibold gap-2;
}

.btn-playback-main {
  @apply h-12 px-6 text-base;
}

.btn-playback-list {
  @apply px-4 bg-zeno-gray-light;
}

.btn-playback-list span {
  @apply text-sm text-zeno-gray;
}

.btn-playback-list svg {
  @apply text-zeno-slider;
}
</style>
